import React, { useState } from 'react';
import styles from './StoreOrdering.module.css';
import { getUnitMeasureLabel } from '../../constants/unitMeasures';

/**
 * OrderCart - Cart-like display of current order items
 * Shows items, running total, and submit button
 */
const OrderCart = ({
  items,
  onItemClick,
  onRemoveItem,
  onSubmitOrder,
  isSubmitting = false,
  isEditing = false,
  onCancelEdit
}) => {
  const [showConfirm, setShowConfirm] = useState(false);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const totalAmount = items.reduce((sum, item) => sum + item.estimatedTotal, 0);
  const hasEstimates = items.some(item => item.isEstimate);

  const handleSubmitClick = () => {
    setShowConfirm(true);
  };

  const handleConfirmSubmit = () => {
    setShowConfirm(false);
    onSubmitOrder();
  };

  if (items.length === 0) {
    return (
      <div className={styles['order-cart']}>
        <div className={styles['order-cart-header']}>
          <h3>{isEditing ? 'Editando Orden' : 'Tu Orden'}</h3>
        </div>
        <div className={styles['empty-cart']}>
          <p>Sin articulos. Toca un producto para agregarlo.</p>
          {isEditing && onCancelEdit && (
            <button className={styles['cancel-edit-btn']} onClick={onCancelEdit}>
              Cancelar
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={styles['order-cart']}>
        <div className={styles['order-cart-header']}>
          <h3>{isEditing ? 'Editando Orden' : 'Tu Orden'}</h3>
          <span className={styles['item-count']}>{items.length} articulo{items.length !== 1 ? 's' : ''}</span>
        </div>

        <div className={styles['order-cart-items']}>
          {items.map((item, index) => (
            <div
              key={`${item.productId}-${index}`}
              className={styles['cart-item']}
              onClick={() => onItemClick(item)}
            >
              <div className={styles['item-info']}>
                <div className={styles['item-name']}>{item.productName}</div>
                <div className={styles['item-details']}>
                  {item.quantity} {item.orderUnit}{item.quantity !== 1 ? 's' : ''}
                  {item.unitMeasure && <> ({getUnitMeasureLabel(item.unitMeasure)})</>}
                  {item.estimatedAmount && item.pricingUnit !== item.orderUnit && (
                    <> • {item.isEstimate ? '~' : ''}{item.estimatedAmount.toFixed(0)} {item.pricingUnit} total</>
                  )}
                </div>
              </div>
              <div className={styles['item-price']}>
                {item.isEstimate ? '~' : ''}{formatCurrency(item.estimatedTotal)}
              </div>
              <button
                className={styles['remove-btn']}
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveItem(index);
                }}
              >
                &times;
              </button>
            </div>
          ))}
        </div>

        <div className={styles['order-cart-footer']}>
          <div className={styles['cart-total']}>
            <span className={styles['total-label']}>Total</span>
            <span className={styles['total-amount']}>
              {hasEstimates ? '~' : ''}{formatCurrency(totalAmount)}
            </span>
          </div>

          {isEditing && onCancelEdit && (
            <button
              className={styles['cancel-edit-btn']}
              onClick={onCancelEdit}
            >
              Cancelar
            </button>
          )}

          <button
            className={styles['send-order-btn']}
            onClick={handleSubmitClick}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? (isEditing ? 'Guardando...' : 'Enviando...')
              : (isEditing ? 'Guardar Cambios' : 'Enviar Orden')
            }
          </button>
        </div>
      </div>

      {/* Confirmation dialog */}
      {showConfirm && (
        <div className={styles['confirm-dialog-overlay']} onClick={() => setShowConfirm(false)}>
          <div className={styles['confirm-dialog']} onClick={e => e.stopPropagation()}>
            <h4>{isEditing ? 'Guardar Cambios?' : 'Enviar Orden?'}</h4>
            <p>
              {isEditing ? 'Actualizar' : 'Enviar'} orden con {items.length} articulo{items.length !== 1 ? 's' : ''}
              ({hasEstimates ? '~' : ''}{formatCurrency(totalAmount)} total)?
            </p>
            <div className={styles['confirm-dialog-actions']}>
              <button
                className={styles['cancel-btn']}
                onClick={() => setShowConfirm(false)}
              >
                Cancelar
              </button>
              <button
                className={styles['confirm-btn']}
                onClick={handleConfirmSubmit}
              >
                {isEditing ? 'Guardar' : 'Enviar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OrderCart;
